import React from 'react';
import { Dialog, DialogContent, Box } from '@mui/material';
import Signup from '../signup/Signup';
import { primitiveGradients, semanticColors, BORDER_RADIUS } from '@/theme/tokens';

interface SignupModalProps {
  open: boolean;
  onSignupComplete: () => void;
  onDismiss: () => void;
}

/**
 * Address / consent signup flow as a modal over the main app (e.g. guest chat).
 * Backdrop click, Escape, and the close icon all call onDismiss (sign out).
 */
const SignupModal: React.FC<SignupModalProps> = ({ open, onSignupComplete, onDismiss }) => {
  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      disableEnforceFocus
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="signup-modal-title"
      sx={{
        zIndex: (theme) => theme.zIndex.modal,
        '& .MuiDialog-paper': {
          borderRadius: BORDER_RADIUS,
          overflow: 'hidden',
          backgroundColor: semanticColors.surface,
          maxWidth: '520px',
          // ``dvh`` tracks the *visible* mobile viewport (``100vh`` is the
          // URL-bar-hidden height, so the paper overflowed the screen top and
          // bottom); subtract the 8px top+bottom margin so it fits and the
          // DialogContent scrolls inside instead.
          //
          // The safe-area insets are on top of that because ``index.html`` sets
          // ``viewport-fit=cover``: without them the 8px margin is measured from
          // the physical screen edge, so on a notched phone in a standalone PWA
          // or an in-app webview the logo at the top of the form renders under
          // the status bar. All four edges, not just the vertical pair — a
          // landscape or split-screen webview reports a non-zero left/right
          // inset, and a side cutout would otherwise clip the fields. The
          // margins and width are complementary (``8px + inset`` each side
          // against ``100% - 16px - left - right``), so the paper still fills
          // the viewport exactly.
          maxHeight: {
            xs: 'calc(100dvh - 16px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            sm: 'min(92vh, 900px)',
          },
          margin: {
            xs: 'calc(8px + env(safe-area-inset-top, 0px)) calc(8px + env(safe-area-inset-right, 0px)) calc(8px + env(safe-area-inset-bottom, 0px)) calc(8px + env(safe-area-inset-left, 0px))',
            sm: 2,
          },
          width: {
            xs: 'calc(100% - 16px - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))',
            sm: 'auto',
          },
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.08)',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <Box sx={{ height: '4px', background: primitiveGradients.primary, flexShrink: 0 }} />

      <DialogContent
        sx={{
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          position: 'relative',
          overflowY: 'auto',
        }}
      >
        <Box sx={{ pt: 0.5 }}>
          <Signup onSignupComplete={onSignupComplete} onGoBack={onDismiss} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SignupModal;
